import { html, nothing } from "lit";
import { icons } from "../icons.ts";
import type { AgentsListResult } from "../types.ts";

export type ProviderFormState = {
  providerId: string;
  customProviderId: string; // For custom provider ID input
  baseUrl: string;
  apiKey: string;
  modelId: string | null;
  fallbackModels: string[];
  showApiKey: boolean;
};

export type AgentProviderProps = {
  agent: AgentsListResult["agents"][number];
  configForm: Record<string, unknown> | null;
  configLoading: boolean;
  configSaving: boolean;
  configDirty: boolean;
  providerState: ProviderFormState;
  credentialsImported: boolean;
  credentialsImporting: boolean;
  credentialsError: string | null;
  onProviderChange: (agentId: string, state: ProviderFormState) => void;
  onConfigReload: () => void;
  onConfigSave: () => void;
  onImportCredentials: (curlCommand: string) => Promise<{ ok: boolean; error?: string }>;
};

// Default provider configurations
const DEFAULT_PROVIDERS = [
  { id: "anthropic", label: "Anthropic (Claude)", baseUrl: "https://api.anthropic.com" },
  { id: "openai", label: "OpenAI (GPT)", baseUrl: "https://api.openai.com/v1" },
  { id: "openrouter", label: "OpenRouter", baseUrl: "https://openrouter.ai/api/v1" },
  { id: "zai", label: "Z.AI (GLM)", baseUrl: "https://open.bigmodel.cn/api/paas/v4" },
  {
    id: "google",
    label: "Google (Gemini)",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta",
  },
  { id: "ollama", label: "Ollama (Local)", baseUrl: "http://localhost:11434" },
  { id: "vllm", label: "vLLM", baseUrl: "http://localhost:8000/v1" },
  { id: "custom", label: "Custom Provider", baseUrl: "" },
];

// Common models for each provider
const PROVIDER_MODELS: Record<string, Array<{ id: string; label: string }>> = {
  anthropic: [
    { id: "claude-sonnet-4-6-20250514", label: "Claude Sonnet 4.6" },
    { id: "claude-opus-4-6-20250514", label: "Claude Opus 4.6" },
    { id: "claude-3-5-sonnet-20241022", label: "Claude 3.5 Sonnet" },
    { id: "claude-3-5-haiku-20241022", label: "Claude 3.5 Haiku" },
  ],
  openai: [
    { id: "gpt-4.1", label: "GPT-4.1" },
    { id: "gpt-4.1-mini", label: "GPT-4.1 Mini" },
    { id: "gpt-4o", label: "GPT-4o" },
    { id: "gpt-4o-mini", label: "GPT-4o Mini" },
    { id: "o3", label: "o3" },
    { id: "o4-mini", label: "o4-mini" },
  ],
  openrouter: [
    { id: "openai/gpt-oss-20b", label: "OpenAI GPT-OSS 20B" },
    { id: "qwen/qwen3-32b", label: "Qwen3 32B" },
    { id: "anthropic/claude-sonnet-4.6", label: "Claude Sonnet 4.6" },
    { id: "anthropic/claude-opus-4.6", label: "Claude Opus 4.6" },
  ],
  zai: [
    { id: "glm-4-plus", label: "GLM-4 Plus" },
    { id: "glm-4-air", label: "GLM-4 Air" },
    { id: "glm-4-flash", label: "GLM-4 Flash" },
    { id: "glm-5", label: "GLM-5" },
    { id: "glm-5-turbo", label: "GLM-5 Turbo" },
  ],
  google: [
    { id: "gemini-2.5-pro", label: "Gemini 2.5 Pro" },
    { id: "gemini-2.5-flash", label: "Gemini 2.5 Flash" },
    { id: "gemini-1.5-pro", label: "Gemini 1.5 Pro" },
    { id: "gemini-1.5-flash", label: "Gemini 1.5 Flash" },
  ],
  ollama: [
    { id: "gpt-oss:120b", label: "GPT-OSS 120B" },
    { id: "llama3.2", label: "Llama 3.2" },
    { id: "llama3.1", label: "Llama 3.1" },
    { id: "mistral", label: "Mistral" },
    { id: "codellama", label: "Code Llama" },
    { id: "qwen2.5", label: "Qwen 2.5" },
    { id: "qwen3.5:35b", label: "Qwen 3.5 35B" },
    { id: "deepseek-r1", label: "DeepSeek R1" },
  ],
  custom: [],
};

export function renderAgentProvider(props: AgentProviderProps) {
  const {
    agent,
    configForm,
    configLoading,
    configSaving,
    configDirty,
    providerState,
    onProviderChange,
    onConfigReload,
    onConfigSave,
  } = props;
  const disabled = configLoading || configSaving;

  const providers =
    (
      configForm?.models as
        | {
            providers?: Record<
              string,
              { baseUrl?: string; apiKey?: string; defaultModelId?: string }
            >;
          }
        | undefined
    )?.providers ?? {};
  const configuredProviders = Object.keys(providers);

  const updateState = (updates: Partial<ProviderFormState>) => {
    onProviderChange(agent.id, { ...providerState, ...updates });
  };

  const models = PROVIDER_MODELS[providerState.providerId] ?? [];

  return html`
    <section class="card">
      <div class="card-title">AI Provider</div>
      <div class="card-sub">Configure API keys, base URLs, and model selection for this agent.</div>

      <div class="agents-provider-form" style="margin-top: 16px;">
        <!-- Quick Import (OneHammer Credentials) -->
        <div class="callout info" style="margin-bottom: 16px;">
          <h3 style="font-size: 14px; font-weight: 600; margin: 0 0 12px;">Quick Import (OneHammer Credentials)</h3>
          <p style="color: var(--text-muted); font-size: 12px; margin-bottom: 12px;">
            Paste the curl command you received to auto-configure the AI provider.
          </p>
          ${
            props.credentialsImported
              ? html`
                  <div
                    style="
                      padding: 12px;
                      background: var(--bg-input);
                      border-radius: 6px;
                      color: var(--text-success);
                      font-weight: 500;
                    "
                  >
                    ✓ AI Provider configured successfully.
                  </div>
                `
              : html`
              <div style="display: flex; flex-direction: column; gap: 8px;">
                <textarea
                  class="input"
                  id="import-credentials-curl"
                  rows="3"
                  placeholder='curl -s -H "Authorization: Bearer <your_token>" "https://your-domain/api/clients/me/claude-credentials?instance=username"'
                  style="width: 100%; font-family: monospace; font-size: 12px; resize: vertical;"
                  ?disabled=${props.credentialsImporting}
                ></textarea>
                <div style="display: flex; align-items: center; gap: 8px; margin-top: 8px;">
                  <button
                    type="button"
                    class="btn btn--sm primary"
                    ?disabled=${props.credentialsImporting}
                    @click=${async () => {
                      const input = document.getElementById(
                        "import-credentials-curl",
                      ) as HTMLTextAreaElement;
                      if (input && props.onImportCredentials) {
                        const result = await props.onImportCredentials(input.value);
                        if (!result.ok) {
                          // Error is handled by the parent component
                        }
                      }
                    }}
                  >
                    ${props.credentialsImporting ? "Importing..." : "Import"}
                  </button>
                  ${
                    props.credentialsError
                      ? html`<span style="color: var(--danger-color, #d14343); font-size: 12px;">${props.credentialsError}</span>`
                      : nothing
                  }
                </div>
              </div>
            `
          }
        </div>

        <!-- Provider Selection -->
        <div class="field">
          <span>Provider</span>
          <select
            .value=${providerState.providerId}
            ?disabled=${disabled}
            @change=${(e: Event) => {
              const val = (e.target as HTMLSelectElement).value;
              const provider = DEFAULT_PROVIDERS.find((p) => p.id === val);
              // Load existing config for this provider
              const providerConfig = providers[val];
              updateState({
                providerId: val,
                baseUrl: providerConfig?.baseUrl ?? provider?.baseUrl ?? "",
                apiKey: providerConfig?.apiKey ?? "",
                modelId: null,
              });
            }}
          >
            <option value="">Select a provider...</option>
            ${DEFAULT_PROVIDERS.map(
              (p) => html`
              <option value=${p.id} ?selected=${p.id === providerState.providerId}>
                ${p.label}${configuredProviders.includes(p.id) ? " ✓" : ""}
              </option>
            `,
            )}
          </select>
        </div>

        ${
          providerState.providerId
            ? html`
          <!-- Custom Provider ID (only for custom provider) -->
          ${
            providerState.providerId === "custom"
              ? html`
            <div class="field">
              <span>Provider ID</span>
              <input
                type="text"
                placeholder="e.g., deepseek, moonshot, my-provider"
                .value=${providerState.customProviderId}
                ?disabled=${disabled}
                @input=${(e: Event) => {
                  updateState({ customProviderId: (e.target as HTMLInputElement).value });
                }}
              />
              <div class="muted" style="font-size: 12px; margin-top: 4px;">
                A unique identifier for this custom provider. Used as prefix in model IDs.
              </div>
            </div>
          `
              : nothing
          }

          <!-- Base URL -->
          <div class="field">
            <span>Base URL</span>
            <input
              type="url"
              placeholder="e.g., https://api.anthropic.com"
              .value=${providerState.baseUrl}
              ?disabled=${disabled}
              @input=${(e: Event) => {
                updateState({ baseUrl: (e.target as HTMLInputElement).value });
              }}
            />
            <div class="muted" style="font-size: 12px; margin-top: 4px;">
              API endpoint for the provider. Leave default for standard providers.
            </div>
          </div>

          <!-- API Key -->
          <div class="field">
            <span>API Key</span>
            <div style="display: flex; align-items: center; gap: 8px;">
              <input
                type=${providerState.showApiKey ? "text" : "password"}
                placeholder="Enter your API key"
                .value=${providerState.apiKey}
                ?disabled=${disabled}
                style="flex: 1;"
                @input=${(e: Event) => {
                  updateState({ apiKey: (e.target as HTMLInputElement).value });
                }}
              />
              <button
                type="button"
                class="btn btn--icon ${providerState.showApiKey ? "active" : ""}"
                style="width: 36px; height: 36px;"
                title=${providerState.showApiKey ? "Hide API key" : "Show API key"}
                @click=${() => updateState({ showApiKey: !providerState.showApiKey })}
              >
                ${providerState.showApiKey ? icons.eye : icons.eyeOff}
              </button>
            </div>
            <div class="muted" style="font-size: 12px; margin-top: 4px;">
              Your API key is stored locally and never sent to our servers.
            </div>
          </div>

          <!-- Model Selection -->
          ${
            models.length > 0
              ? html`
            <div class="field">
              <span>Model</span>
              <select
                .value=${providerState.modelId ?? ""}
                ?disabled=${disabled}
                @change=${(e: Event) => {
                  const val = (e.target as HTMLSelectElement).value;
                  updateState({ modelId: val || null });
                }}
              >
                <option value="">Select a model...</option>
                ${models.map(
                  (m) => html`
                  <option value=${m.id} ?selected=${m.id === providerState.modelId}>
                    ${m.label}
                  </option>
                `,
                )}
              </select>
              <div class="muted" style="font-size: 12px; margin-top: 4px;">
                If your model is not listed (for example Ollama gpt-oss:120b), enter it below.
              </div>
            </div>
            <div class="field">
              <span>Custom Model ID</span>
              <input
                type="text"
                placeholder="e.g., gpt-oss:120b, qwen3:32b"
                .value=${providerState.modelId ?? ""}
                ?disabled=${disabled}
                @input=${(e: Event) => {
                  updateState({ modelId: (e.target as HTMLInputElement).value || null });
                }}
              />
            </div>
          `
              : html`
            <div class="field">
              <span>Model ID</span>
              <input
                type="text"
                placeholder="e.g., gpt-4, claude-3-opus"
                .value=${providerState.modelId ?? ""}
                ?disabled=${disabled}
                @input=${(e: Event) => {
                  updateState({ modelId: (e.target as HTMLInputElement).value || null });
                }}
              />
            </div>
          `
          }
        `
            : nothing
        }

        ${
          configDirty
            ? html`
                <div class="callout warn" style="margin-top: 12px">You have unsaved changes.</div>
              `
            : nothing
        }

        <!-- Actions -->
        <div class="row" style="margin-top: 16px; gap: 8px;">
          <button
            type="button"
            class="btn"
            ?disabled=${configLoading}
            @click=${onConfigReload}
          >
            ${configLoading ? "Loading..." : "Reload"}
          </button>
          <button
            type="button"
            class="btn primary"
            ?disabled=${configSaving || !providerState.providerId}
            @click=${onConfigSave}
          >
            ${configSaving ? "Saving..." : "Save"}
          </button>
        </div>

        <!-- Help text -->
        <div class="muted" style="margin-top: 16px; font-size: 12px;">
          <strong>Tip:</strong> After saving, your provider configuration will be available for all agents.
          You can also configure providers in Settings → AI & Agents for more advanced options.
        </div>
      </div>
    </section>
  `;
}

export function initProviderState(
  configForm: Record<string, unknown> | null,
  agentId: string,
): ProviderFormState {
  // Extract providers config
  const modelsConfig = configForm?.models as
    | { providers?: Record<string, { baseUrl?: string; apiKey?: string; models?: string[] }> }
    | undefined;
  const providers = modelsConfig?.providers ?? {};

  // Extract agent config
  const agentsList = (
    configForm?.agents as { list?: Array<{ id?: string; model?: string }> } | undefined
  )?.list;
  const agentConfig = agentsList?.find((a) => a.id === agentId);

  // Try to detect current provider from agent model string (format: "provider/model")
  const modelStr = agentConfig?.model ?? "";
  let detectedProvider = "";
  let detectedModel = "";
  let customProviderId = "";

  // Parse model string to extract provider and model
  if (modelStr.startsWith("anthropic/")) {
    detectedProvider = "anthropic";
    detectedModel = modelStr.replace("anthropic/", "");
  } else if (modelStr.startsWith("openai/")) {
    detectedProvider = "openai";
    detectedModel = modelStr.replace("openai/", "");
  } else if (modelStr.startsWith("openrouter/")) {
    detectedProvider = "openrouter";
    detectedModel = modelStr.replace("openrouter/", "");
  } else if (modelStr.startsWith("zai/")) {
    detectedProvider = "zai";
    detectedModel = modelStr.replace("zai/", "");
  } else if (modelStr.startsWith("google/")) {
    detectedProvider = "google";
    detectedModel = modelStr.replace("google/", "");
  } else if (modelStr.startsWith("ollama/")) {
    detectedProvider = "ollama";
    detectedModel = modelStr.replace("ollama/", "");
  } else if (modelStr.startsWith("vllm/")) {
    detectedProvider = "vllm";
    detectedModel = modelStr.replace("vllm/", "");
  } else if (modelStr.includes("/")) {
    // Unknown provider prefix - treat as custom
    const slashIndex = modelStr.indexOf("/");
    const providerPrefix = modelStr.substring(0, slashIndex);
    detectedProvider = "custom";
    customProviderId = providerPrefix;
    detectedModel = modelStr.substring(slashIndex + 1);
  } else if (modelStr.includes("claude")) {
    detectedProvider = "anthropic";
    detectedModel = modelStr;
  } else if (
    modelStr.includes("gpt") ||
    modelStr.includes("o1") ||
    modelStr.includes("o3") ||
    modelStr.includes("o4")
  ) {
    detectedProvider = "openai";
    detectedModel = modelStr;
  } else if (modelStr.includes("glm")) {
    detectedProvider = "zai";
    detectedModel = modelStr;
  } else if (modelStr.includes("gemini")) {
    detectedProvider = "google";
    detectedModel = modelStr;
  } else if (
    modelStr.includes("llama") ||
    modelStr.includes("mistral") ||
    modelStr.includes("qwen") ||
    modelStr.includes("deepseek")
  ) {
    detectedProvider = "ollama";
    detectedModel = modelStr;
  } else if (modelStr) {
    // Non-empty model that doesn't match known patterns - treat as custom
    detectedProvider = "custom";
    detectedModel = modelStr;
  }

  // Get first configured provider as fallback if no provider detected
  const configuredProviders = Object.keys(providers);
  const firstConfigured = configuredProviders[0] ?? "";

  // Use detected provider, or first configured, or empty
  const providerId = detectedProvider || firstConfigured || "";
  const provider = DEFAULT_PROVIDERS.find((p) => p.id === providerId);

  // For custom providers, also try to get config from the custom provider ID
  const effectiveProviderKey =
    providerId === "custom" && customProviderId ? customProviderId : providerId;
  const providerConfig = providers[effectiveProviderKey];

  return {
    providerId,
    customProviderId,
    baseUrl: providerConfig?.baseUrl ?? provider?.baseUrl ?? "",
    apiKey: providerConfig?.apiKey ?? "",
    modelId: detectedModel || null,
    fallbackModels: [],
    showApiKey: false,
  };
}
